/* eslint-disable max-len */
import React, { useState, useEffect, useRef, useContext } from 'react';
import { string, bool, object, oneOfType } from 'prop-types';
import { Button } from '@one-thd/sui-atomic-components';
import { withErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { withHydrator } from '@thd-olt-component-react/hydrator';
import { ExperienceContext, useStore, useConfigService } from '@thd-nucleus/experience-context';
import { useDataModel, useLazyDataModel } from '@thd-nucleus/data-sources';
import className from 'classnames';
import { LoadingPlaceholder } from '../core/LoadingPlaceholder';
import { CarouselHeader } from '../core/CarouselHeader';
import { SupplementalTilesCarousel } from './SupplementalTilesCarousel';
import { SupplementalTiles } from './SupplementalTiles';
import { SupplementalRecsPods } from './SupplementalRecsPods';
import { supplementalRecsdataModel } from '../dataModel';
import { publish } from '../utils/publisher';
import useCxtPropsAndOptions from '../hooks/useCxtPropsAndOptions';
import { getAppId, isRecsLoading, getAnalyticsParent } from '../utils/helpers';
import { DtsTntVariables, UseRecsCxtAndProps } from '../tnt-recs/tnt-variables';

const SupplementalRecsLoadingPlaceholder = () => {
  const { isMobile } = useCxtPropsAndOptions();

  return (
    <LoadingPlaceholder
      itemHeight={isMobile ? 44 : 56}
      itemsCount={6}
      txtPlaceholderLines={!isMobile ? 2 : 0}
    />
  );
};

const SupplementalRecsWrapper = (props) => {
  const { anchor, anchorId, schemaName } = props;
  const storeInfo = useStore();
  const { channel, isConsumerApp } = useContext(ExperienceContext);
  const [mounted, setMounted] = useState(false);
  const [tntData, setTntData] = useState(null);
  const abcRecsTestEnable = useConfigService('fs:isABCRecsTestEnable');

  useEffect(() => {
    setMounted(true);
    if (abcRecsTestEnable && typeof LIFE_CYCLE_EVENT_BUS !== 'undefined') {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.on('recs.tnt', (data) => {
        let tntObj = {};
        const output = data?.output;
        if (output?.currentAPI === schemaName) {
          if (output?.isDts) {
            tntObj.isDts = output?.isDts;
            tntObj.isTNT = data?.eventName === 'tnt';
            tntObj.schemaName = output?.dtsModel?.model;
            tntObj.dtsModelVariables = output?.dtsModel;
            tntObj.isDrawerShow = output?.dtsModel?.isDrawerShow;
            tntObj.tntTitle = output?.dtsModel?.tntTitle;
            setTntData(tntObj);
          } else {
            tntObj.isDts = output?.isDts;
            tntObj.isTNT = data?.eventName === 'tnt';
            tntObj.schemaName = output?.recsModel?.testAPI;
            tntObj.isDrawerShow = output?.recsModel?.isDrawerShow;
            tntObj.recsModelVariables = output?.recsModel;
            tntObj.tntTitle = output?.recsModel?.tntTitle;
            setTntData(tntObj);
          }
        }
      });
    }
  }, []);

  if (mounted) {
    const queryProps = {};
    if (abcRecsTestEnable && tntData?.isTNT && tntData?.isDts) {
      const { qName, dtsopts, dtsAnchor } = DtsTntVariables(tntData?.dtsModelVariables, props, storeInfo, channel, isConsumerApp);
      queryProps.qName = qName;
      queryProps.vars = dtsopts;
      queryProps.anchor = dtsAnchor;
    } else if (abcRecsTestEnable && tntData?.isTNT && (tntData?.isDts !== undefined) && !tntData?.isDts) {
      const { qName, recsOpts, recsAnchor } = UseRecsCxtAndProps(tntData?.recsModelVariables, props, storeInfo, channel, isConsumerApp);
      queryProps.qName = qName;
      queryProps.vars = recsOpts;
      queryProps.anchor = recsAnchor;
    }
    return (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <SupplementalRecs {...props} anchor={anchor || anchorId} tntData={tntData} queryProps={queryProps} />
    );
  }

  return null;

};

const SupplementalRecs = (props) => {
  const { channel, isConsumerApp } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const { storeId, storeZip: zipCode, isLocalized } = useStore();
  const {
    anchorId,
    requestKey,
    schemaName,
    showLoading,
    title,
    buttonValue,
    subtitle,
    isDrawerShow,
    noATCFulfillment,
    tntData,
    queryProps,
    parent
  } = props;

  const recsSchema = useRef(tntData?.schemaName || schemaName);
  const recsopts = {
    variables: {
      anchorId,
      appId: getAppId(channel, isConsumerApp),
      apiName: schemaName,
      dataSource: schemaName,
      skipInstallServices: true,
      key: requestKey,
      storeId,
      zipCode
    },

    ssr: false,
    skip: !isLocalized
  };
  const opts = (tntData?.isTNT !== undefined && tntData?.isTNT) ? queryProps?.vars?.opts : recsopts;
  const query = queryProps?.qName || 'recs';
  const analyticsParent = useRef(getAnalyticsParent(tntData, parent));
  const {
    data, variables, loading, error
  } = useDataModel(query, opts);
  const isDrawerShowTNT = useRef(tntData?.isDrawerShow !== undefined ? tntData?.isDrawerShow : isDrawerShow);
  const [viewDetails, setViewDetails] = useState(false);
  const [renderData, setRenderData] = useState(null);
  const [containerTitle, setContainerTitle] = useState(null);
  const evntName = buttonValue || 'shop-the-look';
  const handleViewDetailsClick = () => {
    publish({
      componentName: 'supplemental-recs',
      eventName: 'shop-the-look-click',
      data: {
        eventName: evntName
      }
    });
    setViewDetails(true);
  };
  const [
    getMultiProductSkus,
    {
      data: fallBackRecsData,
      loading: fallBackRecsLoading,
      error: fallBackRecsError
    }
  ] = useLazyDataModel('recs', recsopts);

  const productsCnt = tntData?.isDts ? data?.dts?.products?.length : data?.recs?.products?.length;
  const productsData = tntData?.isDts ? data?.dts : data?.recs;

  const shouldRequestFallback = () => {
    return (tntData?.isTNT && storeId !== '8119' && !loading && (error || !productsCnt > 0));
  };
  const triggerTntAnalytics = (fallBackTrue) => {
    publish({
      componentName: 'recs-tnt',
      eventName: 'fallback-analytics',
      data: {
        testApiValue: recsSchema.current,
        fallbackValue: fallBackTrue || 'f',
        errorValue: (typeof error === 'undefined') ? 'n' : 'y',
        productsCntValue: productsCnt || 0
      }
    });
  };

  useEffect(() => {
    if (!loading && productsCnt > 0) {
      if (tntData?.isTNT) {
        triggerTntAnalytics();
      }
      setRenderData(productsData);
      setContainerTitle(tntData?.tntTitle || title || productsData?.metadata?.title);
      recsSchema.current = tntData?.schemaName ? tntData?.schemaName : schemaName;
      isDrawerShowTNT.current = tntData?.isDrawerShow !== undefined ? tntData?.isDrawerShow : isDrawerShow;
    } else if (!loading && shouldRequestFallback()) {
      getMultiProductSkus();
    }
  }, [data, loading]);

  useEffect(() => {
    if (!fallBackRecsLoading && !fallBackRecsError && fallBackRecsData?.recs?.products?.length > 0) {
      triggerTntAnalytics('t');
      setRenderData(fallBackRecsData?.recs);
      setContainerTitle(title || fallBackRecsData?.recs?.metadata?.title);
      recsSchema.current = schemaName;
      analyticsParent.current = parent || 'supplemental-recs';
      isDrawerShowTNT.current = isDrawerShow;
    }
  }, [fallBackRecsData, fallBackRecsLoading]);

  if (tntData?.isTNT && (!fallBackRecsLoading && fallBackRecsError)) {
    return null;
  }

  if (!tntData?.isTNT && !loading && (error || !data?.recs)) {
    return null;
  }

  const wrapperClasses = className(
    'sui-w-full', 'sui-pt-0', 'sui-pb-0',
    'sui-p-4'
  );

  const buttonText = buttonValue || 'Shop the Look';
  return (
    <div
      id={recsSchema.current}
      data-type="container"
      data-component={`SupplementalRecs-${recsSchema.current}`}
      className={wrapperClasses}
    >
      {
        showLoading && isRecsLoading(renderData, data, fallBackRecsData, loading, fallBackRecsLoading, tntData?.isTNT)
        && <SupplementalRecsLoadingPlaceholder />
      }
      {
        renderData?.products?.length
        && (
          <>
            <CarouselHeader title={containerTitle} subtitle={subtitle} />
            <meta data-prop="name" content={recsSchema.current} />
            <div className="sui-grid sui-grid-cols-1">
              {isMobile
                ? (
                  <SupplementalTiles
                    productsData={renderData?.products}
                    schemaName={recsSchema.current}
                    onViewDetailsClick={() => setViewDetails(true)}
                    onClickShowDrawer={handleViewDetailsClick}
                    isDrawerShow={isDrawerShowTNT.current}
                  />
                )
                : (
                  <SupplementalTilesCarousel
                    productsData={renderData?.products}
                    schemaName={recsSchema.current}
                    onViewDetailsClick={() => setViewDetails(true)}
                    onClickShowDrawer={handleViewDetailsClick}
                    isDrawerShow={isDrawerShowTNT.current}
                  />
                )}
            </div>
            <div className="sui-grid">
              <div className=" sui-mt-8 sm:sui-mt-1 sui-mb-2 sui-mr-auto sui-ml-auto sui-pb-8">
                <Button
                  variant="secondary"
                  onClick={handleViewDetailsClick}
                >
                  {buttonText}
                </Button>
              </div>
            </div>
            {viewDetails && (
              <SupplementalRecsPods
                productsData={renderData?.products}
                storeId={variables?.storeId}
                zipCode={variables?.zipCode}
                schemaName={recsSchema.current}
                isDrawerOpen={viewDetails}
                handleViewDetailsClick={() => setViewDetails(false)}
                podTitle={title}
                noATCFulfillment={noATCFulfillment}
                parent={analyticsParent.current}
              />
            )}
          </>
        )
      }
    </div>
  );
};

SupplementalRecsWrapper.propTypes = {
  anchor: string,
  anchorId: string.isRequired,
  schemaName: string.isRequired
};

SupplementalRecsWrapper.defaultProps = {
  anchor: null
};

SupplementalRecs.propTypes = {
  anchorId: string.isRequired,
  requestKey: string,
  schemaName: string.isRequired,
  showLoading: bool,
  title: string,
  buttonValue: string,
  subtitle: string,
  isDrawerShow: bool,
  noATCFulfillment: bool,
  tntData: oneOfType([object]),
  queryProps: oneOfType([object]),
  parent: string
};

SupplementalRecs.defaultProps = {
  showLoading: true,
  title: null,
  requestKey: null,
  buttonValue: null,
  subtitle: null,
  isDrawerShow: false,
  noATCFulfillment: true,
  tntData: {},
  queryProps: {},
  parent: null
};

SupplementalRecs.dataModel = supplementalRecsdataModel;
SupplementalRecs.displayName = 'SupplementalRecs';

const HydratedSupplementalRecs = withHydrator(
  {
    delay: 1500,
    placeholder: (<SupplementalRecsLoadingPlaceholder />),
    preserveCtxVal: 'clientStore'
  },
  SupplementalRecsWrapper
);

const SupplementalRecommendations = withErrorBoundary(HydratedSupplementalRecs);
SupplementalRecommendations.dataModel = supplementalRecsdataModel;

export { SupplementalRecommendations };
